"use client"

import { useMemo, useState } from "react"

import { asArray } from "@/lib/as-array"
import { useGetJobs } from "openapi/api/endpoints"
import type { Job } from "openapi/api/model"

/** The four tabs, which are the four statuses the contract stores. */
export const JOB_TABS = ["All", "Active", "Paused", "Closed"] as const
export type JobTab = (typeof JOB_TABS)[number]

export type JobRow = {
  id: string
  title: string
  /** "Northwind Health · Engineering" — company and department, already joined. */
  subtitle: string
  location: string
  type: string
  status: string
  /** Counts read mono, as every machine value does. */
  applicants: string
  interviews: string
  /** "12 Jun 26", or nothing when the contract has no date. */
  opened?: string
  updated?: string
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

/** A date the reader can use, or nothing at all — never "Invalid Date". */
function shortDate(iso?: string | null) {
  if (!iso) return undefined
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`
}

/**
 * The jobs list: the rows, the counts each tab would show, and the filters.
 *
 * There is no artboard for this screen — turn 9's "console" is a single job
 * with the Jobs tab lit, not a list of them. It is built as 5a's table, because
 * it is the same kind of screen: a page of records with a search, a couple of
 * filters and a count.
 */
export function useJobs() {
  const [tab, setTab] = useState<JobTab>("All")
  const [search, setSearch] = useState("")
  const [location, setLocation] = useState("all")

  const { data, isLoading, isError } = useGetJobs()

  // `?? []` only guards null; a body that is not an array walks into `.map`.
  const all = useMemo(() => asArray<Job>(data), [data])

  const locations = useMemo(
    () => [...new Set(all.map((job) => job.location).filter(Boolean))].sort() as string[],
    [all],
  )

  /** How many jobs each tab holds, so a tab can say so before it is opened. */
  const counts = useMemo(() => {
    const byStatus = new Map<string, number>()
    for (const job of all) {
      const status = job.status ?? "draft"
      byStatus.set(status, (byStatus.get(status) ?? 0) + 1)
    }
    return {
      All: all.length,
      Active: byStatus.get("active") ?? 0,
      Paused: byStatus.get("paused") ?? 0,
      Closed: byStatus.get("closed") ?? 0,
    } satisfies Record<JobTab, number>
  }, [all])

  const rows = useMemo<JobRow[]>(() => {
    const needle = search.trim().toLowerCase()
    return all
      .filter((job) => tab === "All" || job.status === tab.toLowerCase())
      .filter((job) => location === "all" || job.location === location)
      .filter((job) => {
        if (!needle) return true
        return [job.title, job.company_name, job.department, job.location].some((field) =>
          (field ?? "").toLowerCase().includes(needle),
        )
      })
      .map((job) => ({
        id: job.id,
        title: job.title,
        subtitle: [job.company_name, job.department].filter(Boolean).join(" · ") || "—",
        location: job.location || "—",
        type: job.type ?? "—",
        status: job.status ?? "draft",
        applicants: String(job.applicants ?? 0),
        interviews: String(job.interviews ?? 0),
        opened: shortDate(job.posted_at),
        updated: shortDate(job.updated_at),
      }))
  }, [all, location, search, tab])

  return {
    rows,
    counts,
    locations,
    tab,
    setTab,
    search,
    setSearch,
    location,
    setLocation,
    total: all.length,
    isLoading,
    isError,
    /*
      Whether the list is known at all. `asArray(undefined)` is an empty array,
      so every figure derived from it — the total, the tab counts, the receipt —
      reads as a confident zero while the request is still out and permanently
      after it fails. Callers show a figure only when this is true.
    */
    isCounted: !isLoading && !isError,
  }
}
